"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  type ReactNode,
} from "react";
import { detectTenant } from "@/lib/utils";

/* ───────────────────────────────────────────────
   Types (sync dengan backend types.ts)
   ─────────────────────────────────────────────── */
export type BusinessType =
  | "photography"
  | "salon"
  | "personal"
  | "iot_agriculture"
  | "ecommerce"
  | "restaurant"
  | "education"
  | "healthcare"
  | "general";

export interface TenantDesignTokens {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  textPrimary?: string;
  textSecondary?: string;
  headingFont?: string;
  bodyFont?: string;
  baseFontSize?: string;
  borderRadius?: string;
  buttonRadius?: string;
  cardRadius?: string;
  defaultTheme?: "light" | "dark";
  heroLayout?: "video" | "image" | "slideshow" | "minimal";
  heroImage?: string;
  heroVideo?: string;
  navStyle?: "transparent" | "solid" | "glass";
  customCSS?: string;
}

export interface TenantSettings {
  siteTitle?: string;
  siteDescription?: string;
  contactPhone?: string;
  contactEmail?: string;
  socialLinks?: Record<string, string>;
  design?: TenantDesignTokens;
  layout?: "photography" | "salon" | "personal" | "iot" | "education" | "default";
  features?: {
    booking?: boolean;
    payment?: boolean;
    gallery?: boolean;
    chat?: boolean;
    iotDashboard?: boolean;
    blog?: boolean;
    products?: boolean;
    reviews?: boolean;
    notifications?: boolean;
  };
  customPages?: Record<string, string>;
  [key: string]: any;
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  themeColor: string;
  businessType: BusinessType;
  logoUrl: string;
  settings: TenantSettings;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  tier: "free" | "pro" | "enterprise";
  subscriptionStatus: "active" | "trial" | "expired" | "cancelled";
  subscriptionExpiresAt: string | null;
  maxServices: number;
  maxBookings: number;
  maxStorageBytes: number;
  storageUsedBytes: number;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  notes: string;
}

/* ───────────────────────────────────────────────
   Default tenants (fallback saat API belum siap)
   ─────────────────────────────────────────────── */
const DEFAULT_TENANTS: Record<string, Tenant> = {
  adara: {
    id: "adara", name: "Adara Image Studio", subdomain: "adara",
    themeColor: "#75350E", businessType: "photography",
    logoUrl: "/logo/logo-adaraimage-hitam.png",
    settings: {
      siteTitle: "Adara Image — Luxury Photo & Video",
      layout: "photography",
      design: {
        primaryColor: "#75350E", secondaryColor: "#8B4513",
        accentColor: "#D4A574", backgroundColor: "#000000",
        surfaceColor: "#1C1C1E", defaultTheme: "dark",
        heroLayout: "video", heroVideo: "https://cdn.karpung.com/tenants/adara/hero-background.mp4", navStyle: "transparent",
      },
      features: { booking: true, payment: true, gallery: true, chat: true, reviews: true },
    },
    active: true, createdAt: "", updatedAt: "", tier: "free", subscriptionStatus: "active", subscriptionExpiresAt: null, maxServices: 10, maxBookings: 500, maxStorageBytes: 104857600, storageUsedBytes: 0, ownerName: "", ownerEmail: "", ownerPhone: "", notes: "",
  },
  vasalon: {
    id: "vasalon", name: "VA Salon & Beauty", subdomain: "vasalon",
    themeColor: "#E91E63", businessType: "salon", logoUrl: "",
    settings: {
      siteTitle: "VA Salon — Beauty & Style",
      layout: "salon",
      design: {
        primaryColor: "#E91E63", secondaryColor: "#C2185B",
        accentColor: "#F8BBD0", backgroundColor: "#FFF5F7",
        surfaceColor: "#FFFFFF", defaultTheme: "light",
        heroLayout: "slideshow", navStyle: "solid",
      },
      features: { booking: true, payment: true, chat: true, reviews: true, blog: true },
    },
    active: true, createdAt: "", updatedAt: "", tier: "free", subscriptionStatus: "active", subscriptionExpiresAt: null, maxServices: 10, maxBookings: 500, maxStorageBytes: 104857600, storageUsedBytes: 0, ownerName: "", ownerEmail: "", ownerPhone: "", notes: "",
  },
  ipung: {
    id: "ipung", name: "Ipung Saputra — Portfolio", subdomain: "ipung",
    themeColor: "#2563EB", businessType: "personal", logoUrl: "",
    settings: {
      siteTitle: "Ipung Saputra — Guru & Developer",
      layout: "personal",
      design: {
        primaryColor: "#2563EB", secondaryColor: "#1D4ED8",
        accentColor: "#93C5FD", backgroundColor: "#FFFFFF",
        surfaceColor: "#F8FAFC", defaultTheme: "light",
        heroLayout: "minimal", navStyle: "solid",
      },
      features: { booking: false, payment: false, gallery: true, blog: true },
    },
    active: true, createdAt: "", updatedAt: "", tier: "free", subscriptionStatus: "active", subscriptionExpiresAt: null, maxServices: 10, maxBookings: 500, maxStorageBytes: 104857600, storageUsedBytes: 0, ownerName: "", ownerEmail: "", ownerPhone: "", notes: "",
  },
  karpung: {
    id: "karpung", name: "Karpung — IoT & Agrikultur", subdomain: "karpung",
    themeColor: "#059669", businessType: "iot_agriculture", logoUrl: "",
    settings: {
      siteTitle: "Karpung — Smart Agriculture IoT",
      layout: "iot",
      design: {
        primaryColor: "#059669", secondaryColor: "#047857",
        accentColor: "#6EE7B7", backgroundColor: "#0F172A",
        surfaceColor: "#1E293B", defaultTheme: "dark",
        heroLayout: "image", navStyle: "glass",
      },
      features: { booking: true, payment: true, iotDashboard: true, blog: true },
    },
    active: true, createdAt: "", updatedAt: "", tier: "free", subscriptionStatus: "active", subscriptionExpiresAt: null, maxServices: 10, maxBookings: 500, maxStorageBytes: 104857600, storageUsedBytes: 0, ownerName: "", ownerEmail: "", ownerPhone: "", notes: "",
  },
  smkn3: {
    id: "smkn3", name: "SMKN 3 Kabupaten Tangerang", subdomain: "smkn3",
    themeColor: "#1a5276", businessType: "education", logoUrl: "",
    settings: {
      siteTitle: "SMKN 3 Kabupaten Tangerang",
      layout: "education",
      design: {
        primaryColor: "#1a5276", secondaryColor: "#2980b9",
        accentColor: "#3498db", backgroundColor: "#ffffff",
        surfaceColor: "#f8fafc", defaultTheme: "light",
        heroLayout: "image", navStyle: "solid",
      },
      features: { booking: false, payment: false, gallery: true, blog: true },
    },
    active: true, createdAt: "", updatedAt: "", tier: "free", subscriptionStatus: "active", subscriptionExpiresAt: null, maxServices: 10, maxBookings: 500, maxStorageBytes: 104857600, storageUsedBytes: 0, ownerName: "", ownerEmail: "", ownerPhone: "", notes: "",
  },
};

/* ───────────────────────────────────────────────
   Helper: hex → CSS custom properties
   ─────────────────────────────────────────────── */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { r: 117, g: 53, b: 14 };
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/** 🎨 Apply FULL design tokens ke dokumen — inilah yang membuat tiap tenant UNIK */
function applyDesignTokens(design: TenantDesignTokens, themeColor: string) {
  const root = document.documentElement;
  const d = design;

  // ── Warna ──
  const primary = d.primaryColor || themeColor;
  const { r, g, b } = hexToRgb(primary);
  root.style.setProperty("--color-primary", primary);
  root.style.setProperty("--color-primary-rgb", `${r}, ${g}, ${b}`);
  root.style.setProperty("--color-primary-light", `rgba(${r}, ${g}, ${b}, 0.15)`);
  root.style.setProperty("--color-primary-dark", `rgba(${r}, ${g}, ${b}, 0.85)`);
  root.style.setProperty("--color-primary-surface", `rgba(${r}, ${g}, ${b}, 0.06)`);
  root.style.setProperty("--color-primary-border", `rgba(${r}, ${g}, ${b}, 0.2)`);

  if (d.secondaryColor) {
    root.style.setProperty("--color-secondary", d.secondaryColor);
  }
  if (d.accentColor) {
    root.style.setProperty("--color-accent", d.accentColor);
  }

  // ── Latar ──
  if (d.backgroundColor) {
    root.style.setProperty("--color-tenant-bg", d.backgroundColor);
  }
  if (d.surfaceColor) {
    root.style.setProperty("--color-tenant-surface", d.surfaceColor);
  }
  if (d.textPrimary) {
    root.style.setProperty("--color-tenant-text", d.textPrimary);
  }
  if (d.textSecondary) {
    root.style.setProperty("--color-tenant-text-secondary", d.textSecondary);
  }

  // ── Tipografi ──
  if (d.headingFont) {
    root.style.setProperty("--font-tenant-heading", d.headingFont);
  }
  if (d.bodyFont) {
    root.style.setProperty("--font-tenant-body", d.bodyFont);
  }
  if (d.baseFontSize) {
    root.style.setProperty("--font-tenant-base", d.baseFontSize);
  }

  // ── Radius ──
  if (d.borderRadius) {
    root.style.setProperty("--radius-tenant", d.borderRadius);
  }
  if (d.cardRadius) {
    root.style.setProperty("--radius-tenant-card", d.cardRadius);
  }
  if (d.buttonRadius) {
    root.style.setProperty("--radius-tenant-btn", d.buttonRadius);
  }

  // ── Custom CSS injection ──
  const existingStyle = document.getElementById("tenant-custom-css");
  if (existingStyle) existingStyle.remove();
  if (d.customCSS) {
    const style = document.createElement("style");
    style.id = "tenant-custom-css";
    style.textContent = d.customCSS;
    document.head.appendChild(style);
  }

  // ── PWA theme-color meta ──
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", primary);
}

/* ───────────────────────────────────────────────
   Context
   ─────────────────────────────────────────────── */
interface TenantContextValue {
  tenant: Tenant;
  loading: boolean;
  hasFeature: (feature: keyof NonNullable<TenantSettings["features"]>) => boolean;
  design: TenantDesignTokens;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: DEFAULT_TENANTS.adara,
  loading: true,
  hasFeature: () => true,
  design: {},
});

export function useTenant() {
  return useContext(TenantContext);
}

/* ───────────────────────────────────────────────
   Provider
   ─────────────────────────────────────────────── */
const KARPUNG_VERSION = "2.1.0";
export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant>(() => {
    const sub = detectTenant("adara");
    return DEFAULT_TENANTS[sub] || DEFAULT_TENANTS.adara;
  });
  const [loading, setLoading] = useState(true);

  // ⚡ Set judul tab SEGERA — tidak nunggu useEffect
  if (typeof document !== 'undefined') {
    document.title = tenant.settings.siteTitle || tenant.name; console.debug("Karpung",KARPUNG_VERSION);
  }

  useEffect(() => {
    const API_BASE = (import.meta.env.VITE_API_URL || "") + "/api";
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 3000);

    fetch(`${API_BASE}/tenant?tenant=${detectTenant("adara")}`, { signal: ctrl.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (json?.data) {
          const t = json.data as Tenant;
          setTenant(t);
          applyDesignTokens(t.settings.design || {}, t.themeColor);
        } else {
          const sub = detectTenant("adara");
          const fallback = DEFAULT_TENANTS[sub] || DEFAULT_TENANTS.adara;
          setTenant(fallback);
          applyDesignTokens(fallback.settings.design || {}, fallback.themeColor);
        }
      })
      .catch(() => {
        const sub = detectTenant("adara");
        const fallback = DEFAULT_TENANTS[sub] || DEFAULT_TENANTS.adara;
        setTenant(fallback);
        applyDesignTokens(fallback.settings.design || {}, fallback.themeColor);
      })
      .finally(() => {
        clearTimeout(timeout);
        setLoading(false);
      });

    return () => {
      clearTimeout(timeout);
      ctrl.abort();
    };
  }, []);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenant,
      loading,
      hasFeature: (feature) => tenant.settings.features?.[feature] !== false,
      design: tenant.settings.design || {},
    }),
    [tenant, loading],
  );

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}
